import React, { useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Task } from '../types';
import './Calendar.css';

interface CalendarProps {
  tasks: Task[];
  onTaskAdd: (date: Date) => void;
  onTaskMove: (taskId: number, newDate: Date) => void;
  onTaskSelect: (task: Task) => void;
}

const Calendar: React.FC<CalendarProps> = ({ tasks, onTaskAdd, onTaskMove, onTaskSelect }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const events = tasks.map(task => ({
    id: task.id.toString(),
    title: task.actionLabel.replace('Publicar en ', ''),
    date: task.datetimePlanned,
    datePublicated: task.datetimePublished,
    extendedProps: { taskId: task.id, articleId: task.article.id, postName: task.article.title }
  }));

  const handleDateClick = (arg: { date: Date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (arg.date < today) {
      alert('Esa fecha se encuentra en el pasado.');
      return;
    }
    onTaskAdd(arg.date);
  };

  const handleEventDrop = (info: any) => {
    const taskId = parseInt(info.event.id);
    const task = tasks.find(t => t.id === taskId);

    if (task?.datetimePublished && !isNaN(new Date(task.datetimePublished).getTime())) {
      alert('Esta tarea ya ha sido ejecutada.');
      info.revert();
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (info.event.start < today) {
      alert('Esa fecha se encuentra en el pasado.');
      info.revert();
      return;
    }

    const newDate = info.event.start;
    onTaskMove(taskId, newDate);
  };

  const handleEventClick = (info: any) => {
    const task = tasks.find(t => t.id.toString() === info.event.id);
    if (task) {
      onTaskSelect(task);
    }
  };

  const renderEventContent = (eventInfo: any) => {
    const eventDate = new Date(eventInfo.event.start).toLocaleTimeString().substring(0, 5) + ' hs';

    return (
      <div>
        <div>{eventInfo.event.title} - {eventDate}</div>
        <div style={{ maxWidth: '100%', fontSize: '0.8em', color: 'white' }}>{eventInfo.event.extendedProps.postName}</div>
      </div>
    );
  };

  const eventClassNames = (eventInfo: any) => {
    const isPublished = eventInfo.event.extendedProps.datePublicated;
    if (isPublished && !isNaN(new Date(isPublished).getTime())) {
      return ['event-published'];
    }
    return [];
  };

  const dayCellClassNames = (day: any) => {
    const today = new Date();
    if (
      day.date.getFullYear() === today.getFullYear() &&
      day.date.getMonth() === today.getMonth() &&
      day.date.getDate() === today.getDate()
    ) {
      return ['today-highlight'];
    }
    return [];
  };
 
  return (
    <div className="calendar-wrapper">
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale="es"
        events={events}
        editable={true}
        selectable={true}
        dateClick={handleDateClick}
        eventDrop={handleEventDrop}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        eventClassNames={eventClassNames}
        dayCellClassNames={dayCellClassNames}
        headerToolbar={isMobile ? {
          left: 'prev,next',
          center: 'title',
          right: 'today'
        } : {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek,dayGridDay'
        }}
        buttonText={{
          today: 'hoy',
          month: 'mes',
          week: 'semana',
          day: 'día'
        }}
        titleFormat={isMobile ? {
          year: 'numeric',
          month: 'short'
        } : {
          year: 'numeric',
          month: 'long'
        }}
        height={isMobile ? 500 : undefined}
        aspectRatio={isMobile ? 0.8 : 1.35}
      />
    </div>
  );
};

export default Calendar;
